import { assertAttendanceAccess } from 'v3/pages/ControlTower/Kanban/Attendance/access';
import { attendancePayload } from 'v3/pages/ControlTower/Kanban/Attendance/session';

import api, { cooperplaceApi } from 'services/api';
import cargonApi from 'services/apis/cargon';
import { createContext } from 'utils/react-helpers';
import { convertNumberToDatabase } from 'v3/utils/formatter';
import moment from 'moment';
import { toReplacementPayload } from 'v3/components/FreightValues/utils';
import loadSchema from './validator';

export async function fetchSelectOptions(url, params = {}) {
  try {
    const { data } = await api.get(url, { params });

    return data;
  } catch (err) {
    return [];
  }
}

export async function fetchCountries(setCountries) {
  try {
    const { data } = await api.get('countries');
    setCountries(data);
  } catch (err) {
    setCountries([]);
  }
}

export const freightTypes = [
  { label: 'Por viagem', value: 'CARGA_FECHADA' },
  { label: 'Por tonelada', value: 'POR_TONELADA' },
];

export const LoadContext = createContext({
  name: 'Add Load',
  strict: false,
});

async function validate(data, setErrors) {
  const errorList = {};
  try {
    await loadSchema.validate(data, {
      abortEarly: false,
      context: {
        isPreloadWithGRO: data?.isPreloadWithGRO,
        limitSchedule: data?.costCenter?.limit_schedule,
      },
    });
    setErrors({ ...errorList });
    return { ok: true, errors: errorList };
  } catch (err) {
    if (err?.inner?.length) {
      err.inner.forEach((error) => {
        errorList[error.path] = error.message;
      });
    } else if (err?.path) {
      errorList[err.path] = err.message || 'Verifique os campos obrigatórios!';
    }

    const firstMessage =
      err?.inner?.[0]?.message || Object.values(errorList)[0];

    setErrors(errorList);
    return { ok: false, errors: errorList, message: firstMessage };
  }
}

export async function checkClientCreditLimit(clientId, freight, setErrors) {
  try {
    const response = await api.get(
      `v3/client/${clientId}/validateCreditLimit?freightValue=${+freight}`,
    );
    if (!response.data.hasLimit) {
      setErrors((prev) => ({ ...prev, client: 'Cliente sem limite de crédito' }));
    }
    return response.data?.hasLimit;
  } catch (error) {
    return false;
  }
}

const saveTollsPlaza = async (tollsPlaza, loadId) => {
  try {
    const payload = {
      loadId: loadId,
      requestError: tollsPlaza?.requestError,
      tollsPlaza: tollsPlaza?.tollsPlaza,
      totalsTollsPlaza: {
        totalTollValue: tollsPlaza?.totalTollValue,
        totalTollTagValue: tollsPlaza?.totalTollTagValue,
        totalKm: tollsPlaza?.totalKm,
        expectedTime: tollsPlaza?.expectedTime,
        identifierHistory: tollsPlaza?.identifierHistory,
        codPolilyne: tollsPlaza?.codPolilyne,
        totalAxes: tollsPlaza?.totalAxes,
      }
    };

    const response = await cooperplaceApi.post('tollsPlaza/insert-tolls-plaza-load', payload);

    return response.data;

  } catch (error) {
    // ignore
  }
};

export async function handleCreation(
  data,
  freightValues,
  replacementFreightValues,
  tollsPlazaInfos,
  setErrors,
  showSnack,
  setLoading,
  setShowModal,
  setRegisteredLoad,
  isPreLoad,
) {
  let savedAttendance = null;
  try {
    if (isPreLoad) await assertAttendanceAccess(data.controlTower);
    const serializePolylineWaypoints = (polylineWaypoints) => {
      if (!polylineWaypoints) return null;
      if (typeof polylineWaypoints === 'string') return polylineWaypoints;
      return JSON.stringify(polylineWaypoints);
    };

    setLoading(true);
    const validation = await validate(data, setErrors);
    if (!validation.ok) {
      showSnack(
        validation.message || 'Verifique os campos obrigatórios!',
        { type: 'error' },
        'label-error-campos-obrigatorios',
      );
      return false;
    }

    if (!freightValues) {
      showSnack(
        'Verifique os campos obrigatórios do layout de frete!',
        { type: 'error' },
        'label-error-campos-obrigatorios',
      );
      return false;
    }

    if (!data?.polylineRoute) {
      showSnack(
        'A rota informada não é válida, verifique os endereços informados na roteirização',
        { type: 'error' },
        'label-error-rota-invalida'
      );
      return false;
    }

    if (data?.isReplacement && !replacementFreightValues) {
      showSnack(
        'Verifique os campos obrigatórios da troca de nota!',
        { type: 'error' },
        'label-error-campos-obrigatorios',
      );
      return false;
    }

    const replacementPayload =
      data?.isReplacement &&
      replacementFreightValues &&
      toReplacementPayload(replacementFreightValues);



    if (!(await checkClientCreditLimit(data.client.id, +data?.total_taker_value, setErrors))) {
      showSnack(
        'Cliente sem limite de crédito! Não é possível cadastrar carga para tomadores sem limite de crédito!',
        { type: 'error' },
        'label-error-cliente-sem-credito',
      );
      return false;
    }

    if (!data?.negotiation?.active) {
      showSnack(
        'A negociação não está ativa!',
        { type: 'error' },
        'label-error-negociacao-ativa'
      );
      setErrors(prev => ({ ...prev, negotiation: 'Negociação não está ativa.' }));
      return false;

    } else if (data?.negotiation?.validityFinal < new Date()) {
      showSnack(
        'A negociação não é mais válida!',
        { type: 'error' },
        'label-error-negociacao-invalida'
      );
      setErrors(prev => ({ ...prev, negotiation: 'Negociação não é mais válida.' }));
      return false;
    }

    let originList = {};
    let destinationList = {};
    let payload = {};

    if (isPreLoad) {
      originList = {
        address: data.origin?.address,
        number: data.origin?.number,
        complement: data.origin?.complement,
        zipCode: data.origin?.zip_code,
        lat: data.origin?.lat,
        lng: data.origin?.lng,
        cityId: data?.origin?.city?.id || null,
        city: data?.origin?.city?.name || null,
        province: data?.origin?.province,
        neighborhood: data.origin?.neighborhood,
        formattedAddress: data.origin?.formatted_address,
        startSchedule: moment(data?.origin.start_schedule),
        scheduledTime: moment(data.origin?.scheduled_time),
        clientId: data?.origin?.client?.id || null,
        countrySlug: data?.origin?.country?.abbreviation,
      };
      destinationList = data.destinations.map((dest) => ({
        address: dest?.address,
        number: dest?.number,
        complement: dest?.complement,
        zipCode: dest?.zip_code,
        province: dest?.province,
        lat: dest?.lat,
        lng: dest?.lng,
        scheduledTime: moment(dest?.scheduled_time),
        cityId: dest?.city?.id,
        city: dest?.city?.name,
        neighborhood: dest?.neighborhood,
        formattedAddress: dest?.formatted_address,
        countrySlug: dest?.country?.abbreviation,
        action: dest?.type?.id,
        startSchedule: moment(dest?.start_schedule),
        clientId: dest.client?.id || null,
      }));
      payload = {
        id: data?.preLoadId,
        negotiatorId: data.negotiator[0]?.id,
        personClientId: data.client.id,
        productId: data?.isPreloadWithGRO
          ? data?.preLoadPolicy?.selectedProduct.id
          : data.product.id,
        branchOfficeId: data.branchOffice?.id,
        container: data?.container,
        costCenterId: data.costCenter?.id,
        takerValue: freightValues?.customer?.takerValue?.value,
        negotiationId: data?.negotiation?.id || null,
        dispositionId: data.selectedDisposition.id,
        contactInfo: `+${data.country.ddi} ${data.phone}`,
        currency: freightValues?.currency?.value || 'BRL',
        loadWeight: +convertNumberToDatabase(data.weight),
        dueDate: data.dueDate ? moment(data.dueDate, 'DD/MM/YYYY HH:mm') : null,
        searchDriver: data?.searchDriver ? 1 : 0,
        searchDriverRadius: data?.searchDriver
          ? data?.searchDriverRadius
            ? Number(data?.searchDriverRadius)
            : 500
          : 0,
        clientOrderNumber: data?.clientOrderNumber || null,
        externalInvoiceNumber: data?.externalInvoiceNumber || null,
        comments: data.observations,
        crawledLoad: data.tracked,
        tolledLoad: data?.isTollIncluded || null,
        tollValue: freightValues?.customer?.tollValue?.value,
        netWeight: +convertNumberToDatabase(data.net_weight),
        dischargeValue: freightValues?.customer?.dischargeValue?.value,
        insuranceValue: freightValues?.discounts?.coopercargaInsurance?.value,
        mopp: data.mopp ? 1 : 0,
        gris: freightValues?.customer?.gris?.value,
        adValorem: freightValues?.customer?.adValorem?.value,
        advaloremIncluded: data.isAdValoremIncluded || null,
        ferry: freightValues?.customer?.ferry?.value,
        icms: freightValues?.customer?.ICMS?.value,
        chargeValue: freightValues?.customer?.chargeValue?.value,
        palletNumber: data.pallets || null,
        isEscorted: data.isEscorted,
        pcpObservation: freightValues?.shipmentTransfers?.pcpObservation,
        totalShipmentTransfer: freightValues?.shipmentTransfers?.total,
        totalInputs: freightValues?.discounts?.total,
        totalShipmentDiscounts: freightValues?.shipmentDiscounts?.total,
        icmsPercentage: freightValues?.customer?.ICMS?.percentage,
        grisPercentage: freightValues?.customer?.gris?.percentage,
        adValoremPercentage: freightValues?.customer?.adValorem?.percentage,
        trackerId: data.tracker ? data.tracker.id : null,
        withBait: data.withBait,
        baitNumber: data.baitNumber ? data.baitNumber : null,
        isTollPayerClient: data?.isTollPayerClient || 0,
        unloadIncluded: data?.isDischargeIncluded || null,
        chargeIncluded: data?.isChargeIncluded || null,
        fareCompany: freightValues?.fareCompany,
        productTypeId: data.product.product_type_id,
        cargoValue: +data?.cargoValue,
        loadVolume: String(data.volume),
        insuranceIncluded: data.isInsuranceIncluded || null,
        typeValue: data?.freightType?.value,
        grisIncluded: data.isGrisIncluded || null,
        adValoremIncluded: data.isAdValoremIncluded | null,
        exchange: +data?.exchange || 0,
        loadCurrency: data?.load_currency?.value || 'BRL',
        routeId: data?.routes?.id || null,
        crt: data?.crt,
        crts: data?.crts
          ? data?.crts.map(crt => ({
            number: crt.number,
            personId: crt.customer.id,
          }))
          : null,
        temperature: data?.temperature || null,
        freightExchange: freightValues?.freightExchange,
        exchangeDay: freightValues?.exchangeDay,
        totalTakerValue: freightValues?.customer?.total,
        loadValueType: freightValues?.loadValueType?.value,
        negotiationType: freightValues?.negotiationType?.value,
        actionTollId: freightValues?.customer?.tollValue?.action?.id || null,
        actionDischargeId: freightValues?.customer?.dischargeValue?.action?.id || null,
        actionChargeId: freightValues?.customer?.chargeValue?.action?.id || null,
        actionGrissAdvaloremId: freightValues?.customer?.gris?.action?.id || null,
        actionIcmsId: freightValues?.customer?.ICMS?.action?.id || null,
        actionFerryId: freightValues?.customer?.ferry?.action?.id || null,
        negotiatorIds: data.negotiator.map((item) => item.id),
        extraFields:
          freightValues?.customer?.extras?.length > 0
            ? freightValues?.customer?.extras?.map(extra => {
              return {
                label: extra?.label,
                type: 0,
                value: extra?.value,
                showToDriver: extra?.showToDriver,
              };
            })
            : null,
        vehicleTypeIds: data.selectedVehicles.map((type) => type.id),
        vehicleImplementTypeIds: data.selectedImplements
          ? data.selectedImplements.map((implement) => implement.id)
          : [],
        vehicleBodyTypeIds: data.selectedBodies.map((body) => body.id),
        tagIds: data.tags?.map((tag) => tag.id),
        automationIds: data.selectedAutomations.map((automation) => automation.id),
        riskManagerIds: data.riskManager ? data.riskManager.map((item) => item.id) : [],
        loadOrigins: originList,
        loadDestinations: destinationList,
        policyId: data?.operationPreLoad?.data?.operation?.policy?.id,
        policyOperationId: data?.operationPreLoad?.data?.operation?.id,
        isReplacement: data?.isReplacement,
        replacements: replacementPayload ? [replacementPayload] : [],
        loadPublish: data?.loadPublish,
        polylineRoute: data?.polylineRoute || null,
        polylineWaypoints: serializePolylineWaypoints(data?.polylineWaypoints),
        totalDistanceInKm: data?.totalDistanceInKm || 0,
      };
    } else {
      originList = {
        address: data.origin?.address,
        number: data.origin?.number,
        complement: data.origin?.complement,
        zip_code: data.origin?.zip_code,
        province: data?.origin?.province,
        lat: data.origin?.lat,
        lng: data.origin?.lng,
        scheduled_time: moment(data.origin?.scheduled_time),
        status: 'current',
        city_id: data?.origin?.city?.id || null,
        city: data?.origin?.city?.name,
        neighborhood: data.origin?.neighborhood,
        country_slug: data?.origin?.country?.abbreviation,
        formatted_address: data.origin?.formatted_address,
        start_schedule: moment(data?.origin.start_schedule),
        client_id: data?.origin?.client?.id || null,
      };

      destinationList = data.destinations.map((dest) => ({
        address: dest?.address,
        number: dest?.number,
        complement: dest?.complement,
        zip_code: dest?.zip_code,
        province: dest?.province,
        lat: dest?.lat,
        lng: dest?.lng,
        scheduled_time: moment(dest?.scheduled_time),
        status: 'current',
        city_id: dest?.city?.id,
        city: dest?.city?.name,
        neighborhood: dest?.neighborhood,
        formatted_address: dest?.formatted_address,
        country_slug: dest?.country?.abbreviation,
        action: dest?.type?.id,
        start_schedule: moment(dest?.start_schedule),
        client_id: dest.client?.id || null,
      }));

      payload = {
        load_weight: +convertNumberToDatabase(data.weight),
        net_weight: +convertNumberToDatabase(data.net_weight),
        monitoring: 1,
        tracking: 1,
        crawled_load: data.tracked,
        tracker_id: data.tracker ? data.tracker.id : null,
        is_escorted: data.isEscorted,
        with_bait: data.withBait,
        bait_number: data.baitNumber ? data.baitNumber : null,
        tolled_load: data?.isTollIncluded || null,
        is_toll_payer_client: data?.isTollPayerClient || 0,
        unload_included: data?.isDischargeIncluded || null,
        charge_included: data?.isChargeIncluded || null,
        contact_info: `+${data.country.ddi} ${data.phone}`,
        negotiator_id: data.negotiator.map((item) => item.id),
        fare_company: freightValues?.fareCompany,
        comments: data.observations,
        person_client_id: data.client.id,
        product_id: data.product.id,
        product_type: data.product.product_type_id,
        branch_office_id: data.branchOffice?.id,
        container: data?.container,
        cargo_value: +data?.cargoValue,
        load_volume: data.volume,
        pallet_number: data.pallets || null,
        vehicleTypes: data.selectedVehicles.map((type) => type.id),
        vehicle_implement: data.selectedImplements
          ? data.selectedImplements.map((implement) => implement.id)
          : [],
        originList,
        destinationList,
        bodyTypes: data.selectedBodies.map((body) => body.id),
        selectedTagList: data.tags?.map((tag) => ({ value: tag.id })),
        due_date: data.dueDate ? moment(data.dueDate, 'DD/MM/YYYY HH:mm') : null,
        search_driver: data?.searchDriver ? 1 : 0,
        search_driver_radius: data?.searchDriver
          ? data?.searchDriverRadius
            ? Number(data?.searchDriverRadius)
            : 500
          : 0,
        orderedStepsDue: [],
        automations: data.selectedAutomations.map((automation) => automation.id),
        toll_value: freightValues?.customer?.tollValue?.value,
        discharge_value: freightValues?.customer?.dischargeValue?.value,
        charge_value: freightValues?.customer?.chargeValue?.value,
        insurance_included: data.isInsuranceIncluded || null,
        insurance_value: +data.insurance || 0,
        mopp: data.mopp,
        loadClearances: data.riskManager ? data.riskManager.map((item) => item.id) : [],
        pricingOrders: data?.pricingOrders
          ?.filter((order) => order.price && order.due_date)
          ?.map((order) => ({
            company_order_id: order.company_order_id,
            due_date: moment(order.due_date, 'DD/MM/YYYY HH:mm'),
            price: +order.price,
          })),
        type_value: data?.freightType?.value,
        taker_value: freightValues?.customer?.takerValue?.value,
        load_disposition_id: data.selectedDisposition.id,
        gris: freightValues?.customer?.gris?.value,
        ad_valorem: freightValues?.customer?.adValorem?.value,
        gris_included: data.isGrisIncluded || null,
        ad_valorem_included: data.isAdValoremIncluded | null,
        extra_fields:
          freightValues?.customer?.extras?.length > 0
            ? freightValues?.customer?.extras?.map(extra => {
              return {
                label: extra?.label,
                type: 0,
                value: extra?.value,
                show_to_drivers: extra?.showToDriver,
              };
            })
            : null,
        cost_center_id: data.costCenter?.id,
        exchange: +data?.exchange || 0,
        load_currency: data?.load_currency?.value || 'BRL',
        route_id: data?.routes?.id || null,
        negotiation_id: data?.negotiation?.id || null,
        crt: data?.crt,
        crts: data?.crts
          ? data?.crts.map(crt => ({
            number: crt.number,
            person_id: crt.customer.id,
          }))
          : null,
        client_order_number: data?.clientOrderNumber || null,
        temperature: data?.maxTemperature || null,
        temperature_min: data?.minTemperature || null,
        external_invoice_number: data?.externalInvoiceNumber || null,
        currency: freightValues?.currency?.value || 'BRL',
        freight_exchange: freightValues?.freightExchange || 0,
        exchange_day: freightValues?.exchangeDay,
        total_taker_value: freightValues?.customer?.total,
        total_inputs: freightValues?.discounts?.total,
        total_shipment_transfer: freightValues?.shipmentTransfers?.total,
        total_shipment_discounts: freightValues?.shipmentDiscounts?.total,
        load_value_type: freightValues?.loadValueType?.value,
        negotiation_type: freightValues?.negotiationType?.value,
        ferry: freightValues.customer?.ferry?.value,
        icms: freightValues.customer?.ICMS?.value,
        pcp_observation: freightValues?.shipmentTransfers?.pcpObservation,
        action_toll: freightValues?.customer?.tollValue?.action?.id || null,
        action_discharge: freightValues?.customer?.dischargeValue?.action?.id || null,
        action_griss_advalorem: freightValues?.customer?.gris?.action?.id || null,
        action_charge: freightValues?.customer?.chargeValue?.action?.id || null,
        action_icms: freightValues?.customer?.ICMS?.action?.id || null,
        action_ferry: freightValues?.customer?.ferry?.action?.id || null,
        gris_percentage: freightValues?.customer?.gris?.percentage,
        ad_valorem_percentage: freightValues?.customer?.adValorem?.percentage,
        icms_percentage: freightValues?.customer?.ICMS?.percentage,
        is_replacement: data?.isReplacement,
        replacements: replacementPayload ? [replacementPayload] : [],
        loadPublish: data?.loadPublish,
        polyline_route: data?.polylineRoute || null,
        polyline_waypoints: serializePolylineWaypoints(data?.polylineWaypoints),
        total_distance_in_km: data?.totalDistanceInKm || 0,
      };
    }

    let load;
    for (let i = 0; i < (data.controlTower ? 1 : data.loadCreationNumber); i++) {
      let response;
      if (isPreLoad) {
        response = await cooperplaceApi.put(
          `${data.controlTower ? 'control-tower/' : ''}loads/${data?.preLoadId}/complete`,
          { ...payload, ...attendancePayload(data.controlTower) }
        );

        if (data.controlTower && response?.data?.id) savedAttendance = response.data.id;
        if (data.controlTower && response?.data?.controlTower?.replayed) {
          setRegisteredLoad(response.data.id);
          setShowModal(true);
          return true;
        }
        if (response?.data?.controlTower?.warning)
          showSnack(response.data.controlTower.warning, { type: 'warning' });
        if (response?.data?.id && data?.polylineRoute && tollsPlazaInfos) {
          await saveTollsPlaza(tollsPlazaInfos, response.data.id);
        }
      } else {
        response = await api.post('v3/loads', payload);

        if (response?.data?.id && data?.polylineRoute && tollsPlazaInfos) {
          await saveTollsPlaza(tollsPlazaInfos, response.data.id);
        }
      }

      load = response.data.id;
      const inputsExtraFields = [
        ...freightValues?.discounts?.extras.map((item) => ({
          ...item,
          manual: true,
        })),
      ];

      const transferExtraFields = [
        ...freightValues?.shipmentTransfers?.extras.map((item) => ({
          ...item,
          manual: true,
        })),
      ];
      const discountsExtraFields = [
        ...freightValues?.shipmentDiscounts?.extras.map((item) => ({
          ...item,
          manual: true,
        })),
      ];

      const issueRateAsExtraField = {
        label: 'Taxa de emissão',
        manual: true,
        value: freightValues?.shipmentDiscounts?.issueRate,
        showToDriver: false,
      };

      discountsExtraFields.push(issueRateAsExtraField);

      const hasCoopercargaInsurance = !!freightValues?.discounts?.coopercargaInsurance?.value;

      if (hasCoopercargaInsurance && freightValues?.discounts?.coopercargaInsurance?.value > 0) {
        const newInputsExtraField = {
          label: 'Seguro Coopercarga',
          manual: true,
          value: freightValues?.discounts?.coopercargaInsurance?.value,
          showToDriver: false,
        };
        inputsExtraFields.push(newInputsExtraField);
      }
      try {
        await cooperplaceApi.put('/carg-inputs', {
          loadId: load,
          inputsExtraFields,
        });
        await cooperplaceApi.put('carg-shipment-transfers/', {
          loadId: load,
          transferExtraFields,
        });
        await cooperplaceApi.put('carg-shipment-discounts/', {
          loadId: load,
          discountsExtraFields,
        });
      } catch (err) {
        console.error(err);
      }
    }

    setShowModal(true);
    setRegisteredLoad(load);

    try {
      await cargonApi.post('load/send', { load_id: load });
    } catch (ex) {
      //
    }

    return true;
  } catch (err) {
    if (savedAttendance) {
      setRegisteredLoad(savedAttendance);
      setShowModal(true);
      showSnack(
        'Carga salva. Uma atualização auxiliar falhou; confira os detalhes da carga.',
        { type: 'warning' },
      );
      return true;
    }
    throw err;
  } finally {
    setLoading(false);
  }
}

export async function checkIfBlacklisted(
  id,
  onBlacklistedCallback,
) {
  let res;
  try {
    res = await cooperplaceApi.get(`blacklist/search-person/${id}`);
  } catch {
    return null
  } finally {
    const blacklistInfo = res?.data;
    const isBlacklisted = blacklistInfo != null;
    if (isBlacklisted) {
      onBlacklistedCallback(blacklistInfo);
    }
    return isBlacklisted;
  }
}
